import React, { useState } from 'react'
import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import type { ProposedAction } from './useAssistantChat'
interface ActionCardProps {
  action: ProposedAction
  onConfirm: (action: ProposedAction) => void
  onCancel: () => void
  busy?: boolean
  /** Compact padding / type for the phone drawer. */
  dense?: boolean
}

function formatValue(value: unknown): string {
  if (value == null || value === '') return '—'
  if (typeof value === 'number') return value.toLocaleString('en-US')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function titleFor(action: ProposedAction): string {
  switch (action.type) {
    case 'createLead':
      return 'Create this lead?'
    case 'navigate':
      return 'Go there now?'
    case 'clockIn':
      return 'Clock in now?'
    case 'updateLeadStatus':
      return 'Update lead status?'
    case 'updateInventoryStatus':
      return 'Update inventory status?'
    case 'updateInventoryFields':
      return 'Apply these field changes?'
    case 'applyGraybookPrice':
      return 'Apply Gray Book retail to list price?'
    case 'createInventoryList':
      return 'Create this inventory list?'
    case 'createMarketingCampaign':
      return 'Create this campaign draft?'
    case 'sendMarketingCampaign':
      return 'Send this campaign?'
    case 'createRoutine':
      return 'New routine'
    default:
      return 'Do this?'
  }
}

function yesLabel(action: ProposedAction): string {
  switch (action.type) {
    case 'navigate':
      return 'Yes, open'
    case 'createLead':
      return 'Yes, create lead'
    case 'clockIn':
      return 'Yes, clock in'
    case 'updateLeadStatus':
    case 'updateInventoryStatus':
      return 'Yes, update status'
    case 'updateInventoryFields':
      return 'Yes, save fields'
    case 'applyGraybookPrice':
      return 'Yes, apply price'
    case 'createInventoryList':
      return 'Yes, create list'
    case 'createMarketingCampaign':
      return 'Yes, save draft'
    case 'sendMarketingCampaign':
      return 'Yes, send'
    default:
      return 'Yes, approve'
  }
}

function PreviewRows({ action }: { action: ProposedAction }) {
  if (action.type === 'navigate' && action.payload.path) {
    return (
      <Chip
        size='small'
        label={`Path: ${action.payload.path}`}
        sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
      />
    )
  }

  if (action.type === 'createLead') {
    const rows = [
      action.payload.customerName && { label: 'Customer', value: action.payload.customerName },
      action.payload.companyName && { label: 'Company', value: action.payload.companyName },
      action.payload.email && { label: 'Email', value: action.payload.email },
      action.payload.phone && { label: 'Phone', value: action.payload.phone },
      action.payload.priority && { label: 'Priority', value: action.payload.priority },
      action.payload.notes && { label: 'Notes', value: action.payload.notes },
    ].filter(Boolean) as Array<{ label: string; value: string }>
    return (
      <Stack direction='row' flexWrap='wrap' gap={0.75}>
        {rows.map(row => (
          <Chip
            key={`${row.label}:${row.value}`}
            size='small'
            label={`${row.label}: ${row.value}`}
            sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
          />
        ))}
      </Stack>
    )
  }

  if (action.type === 'updateLeadStatus' || action.type === 'updateInventoryStatus') {
    const label =
      action.type === 'updateLeadStatus'
        ? action.payload.leadLabel || action.payload.leadId
        : action.payload.vehicleLabel || action.payload.vehicleId
    return (
      <Stack spacing={0.75}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Stack direction='row' alignItems='center' flexWrap='wrap' gap={0.75}>
          {action.payload.fromStatus ? (
            <Chip size='small' label={String(action.payload.fromStatus)} variant='outlined' />
          ) : null}
          {action.payload.fromStatus ? (
            <Typography variant='caption' color='text.secondary'>
              →
            </Typography>
          ) : null}
          <Chip size='small' color='primary' label={String(action.payload.status)} />
        </Stack>
        {(action.payload.status === 'Sold' || action.payload.status === 'Pending Sale') && (
          <Typography variant='caption' color='warning.main'>
            Sold / Pending Sale may still need buyer details on the truck form.
          </Typography>
        )}
      </Stack>
    )
  }

  if (action.type === 'updateInventoryFields') {
    return (
      <Stack spacing={0.75}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {action.payload.vehicleLabel || action.payload.vehicleId}
        </Typography>
        <Stack spacing={0.5}>
          {action.payload.fields.map(field => (
            <Box
              key={`${field.field}:${String(field.to)}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 1,
                alignItems: 'baseline',
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                {field.label || field.field}
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 600, textAlign: 'right' }}>
                {field.from !== undefined ? `${formatValue(field.from)} → ` : ''}
                {formatValue(field.to)}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Typography variant='caption' color='text.disabled'>
          Saved as you — not a service account.
        </Typography>
      </Stack>
    )
  }

  if (action.type === 'createInventoryList') {
    return (
      <Stack spacing={0.75}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {action.payload.name}
        </Typography>
        <Chip size='small' label={`${action.payload.inventoryIds.length.toLocaleString('en-US')} trucks`} />
        {action.payload.description ? (
          <Typography variant='caption' color='text.secondary'>
            {action.payload.description}
          </Typography>
        ) : null}
      </Stack>
    )
  }

  if (action.type === 'createMarketingCampaign') {
    return (
      <Stack spacing={0.75}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {action.payload.name}
        </Typography>
        <Stack direction='row' flexWrap='wrap' gap={0.75}>
          <Chip size='small' color='primary' label='Draft' />
          {action.payload.subject ? <Chip size='small' label={action.payload.subject} /> : null}
          {action.payload.featuredVehicleIds?.length ? (
            <Chip
              size='small'
              label={`${action.payload.featuredVehicleIds.length.toLocaleString('en-US')} trucks`}
            />
          ) : null}
        </Stack>
        <Typography variant='caption' color='text.disabled'>
          Saving a draft does not send email.
        </Typography>
      </Stack>
    )
  }

  if (action.type === 'sendMarketingCampaign') {
    return (
      <Stack spacing={0.75}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {action.payload.name || action.payload.campaignId}
        </Typography>
        <Typography variant='caption' color='warning.main'>
          This sends the existing draft.
        </Typography>
      </Stack>
    )
  }

  if (action.type === 'applyGraybookPrice') {
    return (
      <Stack spacing={0.75}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {action.payload.vehicleLabel || action.payload.vehicleId}
        </Typography>
        <Stack direction='row' flexWrap='wrap' gap={0.75}>
          {action.payload.currentListPrice != null ? (
            <Chip
              size='small'
              variant='outlined'
              label={`Current list $${Number(action.payload.currentListPrice).toLocaleString('en-US')}`}
            />
          ) : null}
          <Chip
            size='small'
            color='primary'
            label={`Gray Book retail $${Number(action.payload.retail).toLocaleString('en-US')}`}
          />
          {action.payload.wholesale != null ? (
            <Chip
              size='small'
              label={`Wholesale $${Number(action.payload.wholesale).toLocaleString('en-US')}`}
            />
          ) : null}
        </Stack>
        <Typography variant='caption' color='text.disabled'>
          Sets optic_list_price. Attribution stays on your user.
        </Typography>
      </Stack>
    )
  }

  return null
}

function RunRoutineCard({
  action,
  busy,
  dense,
  onConfirm,
  onCancel,
}: {
  action: Extract<ProposedAction, { type: 'runRoutine' }>
  busy: boolean
  dense: boolean
  onConfirm: (action: ProposedAction) => void
  onCancel: () => void
}) {
  const choices = action.payload.choices.filter(item => item.id)
  const [routineId, setRoutineId] = useState(action.payload.routineId || choices[0]?.id || '')
  const btnSx = {
    minHeight: 30,
    px: 1.25,
    py: 0.25,
    fontSize: '0.75rem',
    textTransform: 'none',
  } as const
  return (
    <Box
      sx={{
        mt: dense ? 1 : 1.25,
        p: dense ? 1 : 1.5,
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant={dense ? 'caption' : 'subtitle2'} sx={{ mb: 1, fontWeight: 700 }}>
        Send a test
      </Typography>
      <FormControl size='small' fullWidth>
        <InputLabel>Routine</InputLabel>
        <Select label='Routine' value={routineId} onChange={event => setRoutineId(String(event.target.value))}>
          {choices.map(item => (
            <MenuItem key={item.id} value={item.id}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
        Emails this assistant’s members now. The next scheduled run stays where it is.
      </Typography>
      <Divider sx={{ my: dense ? 1 : 1.25 }} />
      <Stack direction='row' spacing={1}>
        <Button
          size='small'
          variant='contained'
          disabled={busy || !routineId}
          onClick={() => onConfirm({ type: 'runRoutine', payload: { routineId, choices } })}
          sx={btnSx}
        >
          Send test
        </Button>
        <Button size='small' variant='outlined' disabled={busy} onClick={onCancel} sx={btnSx}>
          Cancel
        </Button>
      </Stack>
    </Box>
  )
}

/** Confirm / decline card for assistant proposed actions. Explicit Yes applies as the signed-in user. */
export const ActionCard: React.FC<ActionCardProps> = ({
  action,
  onConfirm,
  onCancel,
  busy = false,
  dense = false,
}) => {
  const btnSx = {
    minHeight: 30,
    px: 1.25,
    py: 0.25,
    fontSize: '0.75rem',
    textTransform: 'none',
  } as const
  if (action.type === 'createRoutine') return null
  if (action.type === 'runRoutine') {
    return (
      <RunRoutineCard action={action} busy={busy} dense={dense} onConfirm={onConfirm} onCancel={onCancel} />
    )
  }
  return (
    <Box
      sx={{
        mt: dense ? 1 : 1.25,
        p: dense ? 1 : 1.5,
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant={dense ? 'caption' : 'subtitle2'}
        sx={{ mb: dense ? 0.5 : 0.75, fontWeight: 700, fontSize: dense ? '0.8125rem' : undefined }}
      >
        {titleFor(action)}
      </Typography>
      <Box sx={{ mb: dense ? 1 : 1.25 }}>
        <PreviewRows action={action} />
      </Box>
      <Divider sx={{ mb: dense ? 1 : 1.25 }} />
      <Stack direction='row' spacing={1}>
        <Button
          size='small'
          variant='contained'
          disabled={busy}
          onClick={() => onConfirm(action)}
          sx={btnSx}
        >
          {yesLabel(action)}
        </Button>
        <Button
          size='small'
          variant='outlined'
          disabled={busy}
          onClick={onCancel}
          sx={btnSx}
        >
          No
        </Button>
      </Stack>
    </Box>
  )
}
